import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    Skeleton,
    IconButton,
    TextField,
    InputAdornment,
} from "@mui/material";
import "../css/Charts.css";
import { PieChart } from "@mui/x-charts/PieChart";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { dashboardService } from "../services/dashboardService";
import { CHART_TITLES } from "../constants/chartTitles";
import TableSkeleton from "../component/common/TableSkeleton";

const PieCharts = ({ chartData, title, loading }) => {
    const [selectedSliceData, setSelectedSliceData] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [users, setUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [runningTimers, setRunningTimers] = useState({});
    const [loadingJobs, setLoadingJobs] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const formatChargeHoursToHHMM = (decimalHours) => {
        if (!decimalHours) return "00:00";
        const hours = Math.floor(decimalHours);
        const minutes = Math.round((decimalHours - hours) * 60);
        return `${hours}:${minutes.toString().padStart(2, "0")}`;
    };

    const formatBudgetHoursToHHMM = (decimalHours) => {
        if (!decimalHours) return "00:00";
        const hours = Math.floor(decimalHours);
        const fractionalPart = decimalHours - hours;

        let minutes = Math.round(fractionalPart * 100);
        const extraHours = Math.floor(minutes / 60);
        minutes = minutes % 60;

        const totalHours = hours + extraHours;
        return `${String(totalHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    };

    const parseStartTime = (str) => {
        if (!str) return null;
        const [datePart, timePart, ampm] = str.split(" ");
        const [day, month, year] = datePart.split("-");
        const [hourStr, minute, second] = timePart.split(":");

        let hour = parseInt(hourStr, 10);
        if (ampm.toLowerCase() === "pm" && hour !== 12) hour += 12;
        if (ampm.toLowerCase() === "am" && hour === 12) hour = 0;
        return new Date(`${year}-${month}-${day}T${String(hour).padStart(2, '0')}:${minute}:${second}`);
    };

    const getDurationFromNow = (start) => {
        if (!start) return "00:00:00";
        const now = new Date();
        const diff = Math.floor((now - start) / 1000); // in seconds
        const hours = String(Math.floor(diff / 3600)).padStart(2, "0");
        const minutes = String(Math.floor((diff % 3600) / 60)).padStart(2, "0");
        const seconds = String(diff % 60).padStart(2, "0");
        return `${hours}:${minutes}:${seconds}`;
    };

    const formatDate = (dateString) => {
        const [datePart] = dateString.split(' ');
        const [year, month, day] = datePart.split('-');
        return `${day}/${month}/${year}`;
    };

    const formatISODate = (dateString) => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "—";

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    useEffect(() => {
        if (!selectedSliceData) return;

        const fetchData = async () => {
            setLoadingJobs(true);
            try {
                let data;

                // Logic specifically for Jobs By Status (Supports Search)
                if (title === CHART_TITLES.JOBS_BY_STATUS) {
                    data = await dashboardService.getJobsByStatusForDashboard(selectedSliceData, debouncedSearch);
                    setJobs(data);
                }
                // Logic for other charts (Add search support here later)
                else if (title === CHART_TITLES.JOBS_TO_GO) {
                    data = await dashboardService.getJobsByEndDateForDashboard(selectedSliceData, debouncedSearch);
                    setJobs(data);
                } else if (title === CHART_TITLES.BILLABLE_HOURS) {
                    data = await dashboardService.getJobsByCompletionStatusForDashboard(null, null, "", [], 0, selectedSliceData === "Completed");
                    setJobs(data);
                } else if (title === CHART_TITLES.LIVE_LOGIN) {
                    data = await dashboardService.getActiveTasksDetailsForDashboard(selectedSliceData);
                    if (selectedSliceData === "Not Logged In") {
                        const mappedUsers = (data.users || []).map(u => ({
                            id: u.id,
                            fullName: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
                            designation: u.designation || "-",
                            department: u.department || "-",
                            lastStopTime: u.lastStopTime || "-"
                        }));
                        setUsers(mappedUsers);
                        setJobs([]);
                    } else {
                        setJobs(data.tasks || []);
                        const timers = {};
                        (data.tasks || []).forEach((task) => {
                            if (task.startTime) {
                                const parsedTime = parseStartTime(task.startTime);
                                timers[task.id] = getDurationFromNow(parsedTime);
                            }
                        });
                        setRunningTimers(timers);
                    }
                }

            } catch (err) {
                console.error("Failed to fetch data", err);
            } finally {
                setLoadingJobs(false);
            }
        };

        fetchData();
    }, [selectedSliceData, debouncedSearch, title]);

    // Handle slice click
    const handleItemClick = async (event, pieItemIdentifier, item) => {
        const sliceLabel = item?.label;
        if (!sliceLabel) return;

        setSelectedSliceData(sliceLabel);
        setSearchQuery(""); // Reset search on new slice selection
        setDebouncedSearch("");
        setJobs([]);
        setUsers([]);
    };

    useEffect(() => {
        if (!jobs || jobs.length === 0 || title !== CHART_TITLES.LIVE_LOGIN) return;

        const interval = setInterval(() => {
            setRunningTimers((prevTimers) => {
                const updatedTimers = {};
                jobs.forEach((job) => {
                    if (job.startTime) {
                        const parsedTime = parseStartTime(job.startTime);
                        updatedTimers[job.id] = getDurationFromNow(parsedTime);
                    }
                });
                return updatedTimers;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [jobs, title]);

    const userColumns = [
        { label: "User Name", key: "fullName" },
        { label: "Designation", key: "designation" },
        { label: "Department", key: "department" },
        { label: "Last Stop Time", key: "lastStopTime" },
    ];

    // Dynamic Column Configuration
    const columnConfig = {
        [CHART_TITLES.JOBS_BY_STATUS]: [
            {
                label: "Client Name", key: "client",
                render: (job) =>
                    `${job.client ? job.client?.name : ""}`
            },
            { label: "Job Name", key: "name" },
            { label: "Job Type", key: "type" },
            ...(selectedSliceData === "Query"
                ? [{
                    label: "Status Updated", key: "statusUpdatedDate",
                    render: (job) =>
                        `${job.statusUpdatedDate ? formatISODate(job.statusUpdatedDate) : ""}`
                }] : []),
            {
                label: "End Date", key: "expectedEndDate",
                render: (job) =>
                    `${job.expectedEndDate ? formatDate(job.expectedEndDate) : ""}`
            },
            {
                label: "Job Admin",
                key: "assignedUser",
                render: (job) => `${job.assignedUser?.firstName || ""} ${job.assignedUser?.lastName || ""}`,
            },
        ],
        [CHART_TITLES.JOBS_TO_GO]: [
            { label: "Job Name", key: "name" },
            { label: "Job Type", key: "type" },
            { label: "Status", key: "status" },
            ...(selectedSliceData === "Week"
                ? [{
                    label: "End Date", key: "expectedEndDate",
                    render: (job) =>
                        `${job.expectedEndDate ? formatDate(job.expectedEndDate) : ""}`
                }] : []),
            {
                label: "Job Admin",
                key: "assignedUser",
                render: (job) => `${job.assignedUser?.firstName || ""} ${job.assignedUser?.lastName || ""}`,
            },
        ],
        [CHART_TITLES.BILLABLE_HOURS]: [
            { label: "Job Name", key: "name" },
            { label: "Job Type", key: "type" },
            { label: "Status", key: "status" },
            ...(selectedSliceData === "Completed"
                ? [{
                    label: "Charge Hours", key: "chargeHours",
                    render: (job) =>
                        `${job.chargeHours ? formatChargeHoursToHHMM(job.chargeHours || 0) : "00:00"}`
                }] : [{
                    label: "Budget Hours", key: "budgetHours",
                    render: (job) =>
                        `${job.budgetHours ? formatBudgetHoursToHHMM(job.budgetHours || 0) : "00:00"}`
                }]),
            {
                label: "Job Admin",
                key: "assignedUser",
                render: (job) => `${job.assignedUser?.firstName || ""} ${job.assignedUser?.lastName || ""}`,
            },
        ],
        [CHART_TITLES.LIVE_LOGIN]: [
            ...(selectedSliceData === "Job Tasks" ? [
                {
                    label: "Client", key: "job",
                    render: (task) => `${task.job.client.name || ""}`,
                },
            ] : []),
            { label: "Task Title", key: "titles" },
            ...(selectedSliceData === "Job Tasks" ? [
                {
                    label: "Job", key: "job",
                    render: (task) => `${task.job.name || ""}`,
                },
                {
                    label: "Job Type", key: "job",
                    render: (task) => `${task.job.type || ""}`,
                },
                {
                    label: "Status", key: "job",
                    render: (task) => `${task.job.status || ""}`,
                },
            ] : []),
            {
                label: "Running Time",
                key: "startTime",
                render: (task) => runningTimers[task.id] || "00:00:00",
            },
            {
                label: "Job Admin",
                key: "assignedUser",
                render: (task) => `${task.assignedUser?.firstName || ""} ${task.assignedUser?.lastName || ""}`,
            },
        ],
    };

    const columns = columnConfig[title] || [];

    return (
        <Box sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box className="chart-container">
                {loading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 6,
                            gap: 2,
                        }}
                    >
                        <Skeleton variant="circular" width={170} height={170} />
                        <Skeleton variant="text" width="60%" />
                        <Skeleton variant="text" width="42%" />
                    </Box>
                ) : (
                    <Box className="chart-pie-box">
                        <PieChart
                            series={[
                                {
                                    data: chartData,
                                    valueFormatter: (v, { dataIndex }) => {
                                        return title === CHART_TITLES.BILLABLE_HOURS ? `${v.display}h` : `${v.value}`;
                                    },
                                    arcLabel: (arc) => {
                                        // Calculate percentage of this slice
                                        const total = chartData.reduce((sum, item) => sum + item.value, 0);
                                        const percentage = arc.value / total;

                                        // Show label only if slice is >5% of total
                                        return percentage > 0.05
                                            ? title === CHART_TITLES.BILLABLE_HOURS
                                                ? `${arc.display}h`
                                                : `${arc.value}`
                                            : "";

                                        //title === CHART_TITLES.BILLABLE_HOURS ? `${arc.display}h` : `${arc.value}`
                                    },
                                    arcLabelRadius: "65%",
                                    highlightScope: { faded: "none", highlighted: "none" },
                                },
                            ]}
                            margin={{ top: 10, bottom: 45, left: 30, right: 30 }}
                            slotProps={{
                                legend: {
                                    direction: "row",
                                    position: { vertical: "bottom", horizontal: "middle" },
                                    padding: 0,
                                },
                            }}
                            // tooltip={{ trigger: "none" }}
                            tooltip={{ trigger: "item" }}
                            className="pie-chart"
                            onItemClick={handleItemClick}
                        />
                    </Box>
                )}
            </Box>

            {/* Drill-down modal */}
            <Dialog
                open={Boolean(selectedSliceData)}
                onClose={() => setSelectedSliceData(null)}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        className: "chart-dialog-paper"
                    }
                }}
            >
                <DialogTitle component="div" className="chart-dialog-title">
                    <Typography variant="h6">
                        {title !== CHART_TITLES.LIVE_LOGIN ? `${selectedSliceData} Jobs` : `${selectedSliceData}`}
                    </Typography>

                    <IconButton
                        aria-label="close"
                        onClick={() => setSelectedSliceData(null)}
                        className="chart-dialog-close-btn"
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent className="chart-dialog-content">

                    {title !== CHART_TITLES.LIVE_LOGIN && (
                        <Box className="chart-search-bar">
                            <TextField
                                size="small"
                                placeholder="Type to search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                sx={{ width: 280 }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </Box>
                    )}

                    {loadingJobs ? (
                        <Box sx={{ p: 2 }}>
                            <TableSkeleton rows={6} columns={4} />
                        </Box>
                    ) : selectedSliceData === "Not Logged In" ? (
                        users.length === 0 ? (
                            <Typography sx={{ mt: 2, textAlign: "center", color: "text.secondary" }}>
                                No users found.
                            </Typography>
                        ) : (
                            <TableContainer sx={{ flex: 1, maxHeight: "80vh" }}>
                                <Table size="small" stickyHeader className="chart-table">
                                    <TableHead>
                                        <TableRow>
                                            {userColumns.map((col, index) => (
                                                <TableCell key={index}><b>{col.label}</b></TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {users.map((u, index) => (
                                            <TableRow key={u.id || index}>
                                                {userColumns.map((col, i) => (
                                                    <TableCell key={i}>{u[col.key] || "-"}</TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )
                    ) : jobs.length === 0 ? (
                        <Typography sx={{ mt: 2, textAlign: "center", color: "text.secondary" }}>
                            No jobs found for this period.
                        </Typography>
                    ) : (
                        <TableContainer sx={{ flex: 1, maxHeight: "80vh" }}>
                            <Table size="small" stickyHeader className="chart-table">
                                <TableHead>
                                    <TableRow>
                                        {columns.map((col, index) => (
                                            <TableCell key={index}><b>{col.label}</b></TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {jobs.map((job, index) => (
                                        <TableRow key={job.id || index}>
                                            {columns.map((col, index) => (
                                                <TableCell key={index}>
                                                    {col.render ? col.render(job) : job[col.key] ?? "-"}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default PieCharts;